import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { speakingRoomsApi, type SpeakingRoom } from '@/api/speakingRooms';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';

// Voice rooms are joinable a bit before the scheduled time and while running.
function joinable(r: SpeakingRoom): boolean {
  const start = new Date(r.scheduled_at).getTime();
  const end = start + r.duration_min * 60_000;
  const now = Date.now();
  return r.status !== 'cancelled' && r.status !== 'finished' && now >= start - 10 * 60_000 && now <= end;
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function StudentSpeakingRoomsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [err, setErr] = useState('');

  const rooms = useQuery({ queryKey: ['student', 'speaking-rooms'], queryFn: () => speakingRoomsApi.listOpen() });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['student', 'speaking-rooms'] });

  const bookM = useMutation({
    mutationFn: (id: string) => speakingRoomsApi.book(id),
    onSuccess: () => { setErr(''); invalidate(); },
    onError: (e) => setErr(humanizeError(e)),
  });
  const cancelM = useMutation({
    mutationFn: (id: string) => speakingRoomsApi.cancelBooking(id),
    onSuccess: () => { setErr(''); invalidate(); },
    onError: (e) => setErr(humanizeError(e)),
  });

  const items = rooms.data?.items ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader title={t('speakingRooms.title')} subtitle={t('speakingRooms.subtitle')} />
      {err && <Card variant="subtle" className="text-error">{err}</Card>}

      {rooms.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : items.length === 0 ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('speakingRooms.empty')}
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const full = r.seats_taken >= r.capacity;
            const canJoin = r.booked && joinable(r);
            return (
              <Card key={r.id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-[180px] flex-1">
                    <CardTitle>{r.title}</CardTitle>
                    <p className="mt-0.5 text-sm text-on-surface-variant">
                      {fmt(r.scheduled_at)} · {t('speakingRooms.minutes', { count: r.duration_min })}{r.host_name ? ` · ${r.host_name}` : ''}
                    </p>
                    {r.topic && <p className="mt-1 text-sm text-on-surface">{r.topic}</p>}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {r.level && <Pill variant="neutral">{r.level}</Pill>}
                      <Pill variant={full ? 'warning' : 'success'}>
                        <span className="material-symbols-outlined text-sm">group</span>
                        {t('speakingRooms.seats', { taken: r.seats_taken, capacity: r.capacity })}
                      </Pill>
                      {r.booked && <Pill variant="primary">{t('speakingRooms.booked')}</Pill>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {canJoin && (
                      <Button onClick={() => navigate(`/app/speaking/${r.id}`)}>
                        <span className="material-symbols-outlined">login</span>
                        {t('speakingRooms.enter')}
                      </Button>
                    )}
                    {r.booked ? (
                      <Button variant="ghost" size="sm" loading={cancelM.isPending} onClick={() => cancelM.mutate(r.id)}>
                        {t('speakingRooms.cancel')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        disabled={full}
                        loading={bookM.isPending}
                        onClick={() => bookM.mutate(r.id)}
                      >
                        {t(full ? 'speakingRooms.full' : 'speakingRooms.book')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
